'use client';
import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchProductsWithPagination } from '@/hooks/useProducts';
import { ProductBase } from '@/types/product';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatPrice } from '@/utils/priceFormatter';
import StockBadge from '@/components/common/StockBadge';

interface SearchViewProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  className?: string;
}

const SearchView: React.FC<SearchViewProps> = ({
  placeholder = '¿Qué estás buscando?',
  value: externalValue,
  onChange,
  onSearch,
  className = '',
}) => {
  const [internalValue, setInternalValue] = useState(externalValue || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentValue = externalValue !== undefined ? externalValue : internalValue;

  const {
    data: searchResults,
    isLoading,
    error,
  } = useSearchProductsWithPagination(searchTerm, 'es', { page: currentPage, perPage: 10 });

  useEffect(() => {
    if (currentValue.trim().length >= 3) {
      setSearchTerm(currentValue.trim());
      setShowDropdown(true);
      setCurrentPage(1);
    } else {
      setSearchTerm('');
      setShowDropdown(false);
    }
  }, [currentValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (onChange) onChange(newValue);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    if (currentValue.trim().length >= 3) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    // No cerrar el dropdown aquí, solo quitar el foco visual
    setIsFocused(false);
  };

  const handleProductClick = (product: ProductBase) => {
    setShowDropdown(false);
    setIsFocused(false);
    if (onSearch) onSearch(product.title);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const renderPagination = () => {
    if (!searchResults?.pagination || searchResults.pagination.totalPages <= 1) return null;
    const { page, totalPages, hasNext, hasPrev } = searchResults.pagination;
    return (
      <div data-component-id="src/components/ui/SearchView.tsx:101:6" data-component-path="src/components/ui/SearchView.tsx" data-component-line="101" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20gap-2%20p-3%20border-t%20border-%5B%233D1C1A%5D%2F20%20bg-%5B%23FEF5E6%5D%22%7D" className="flex items-center justify-center gap-2 p-3 border-t border-[#3D1C1A]/20 bg-[#FEF5E6]">
        <button data-component-id="src/components/ui/SearchView.tsx:102:8" data-component-path="src/components/ui/SearchView.tsx" data-component-line="102" data-component-file="SearchView.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20px-3%20py-2%20rounded-lg%20bg-%5B%233D1C1A%5D%20text-%5B%23FEF5E6%5D%20font-medium%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20hover%3Abg-%5B%233D1C1A%5D%2F90%20transition-colors%20text-sm%22%2C%22textContent%22%3A%22Anterior%22%7D"
          onClick={() => handlePageChange(page - 1)}
          disabled={!hasPrev}
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#3D1C1A] text-[#FEF5E6] font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3D1C1A]/90 transition-colors text-sm"
        >
          <ChevronLeft data-component-id="src/components/ui/SearchView.tsx:107:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="107" data-component-file="SearchView.tsx" data-component-name="ChevronLeft" data-component-content="%7B%22elementName%22%3A%22ChevronLeft%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" />
          Anterior
        </button>
        <div data-component-id="src/components/ui/SearchView.tsx:110:8" data-component-path="src/components/ui/SearchView.tsx" data-component-line="110" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1%22%7D" className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
            if (pageNum > totalPages) return null;
            return (
              <button data-component-id="src/components/ui/SearchView.tsx:115:14" data-component-path="src/components/ui/SearchView.tsx" data-component-line="115" data-component-file="SearchView.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`px-3 py-2 rounded-lg font-medium transition-colors text-sm ${
                  pageNum === page
                    ? 'bg-[#3D1C1A] text-[#FEF5E6]'
                    : 'bg-[#FEF5E6] text-[#3D1C1A] hover:bg-[#3D1C1A]/10 border border-[#3D1C1A]/20'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
        <button data-component-id="src/components/ui/SearchView.tsx:129:8" data-component-path="src/components/ui/SearchView.tsx" data-component-line="129" data-component-file="SearchView.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20px-3%20py-2%20rounded-lg%20bg-%5B%233D1C1A%5D%20text-%5B%23FEF5E6%5D%20font-medium%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20hover%3Abg-%5B%233D1C1A%5D%2F90%20transition-colors%20text-sm%22%2C%22textContent%22%3A%22Siguiente%22%7D"
          onClick={() => handlePageChange(page + 1)}
          disabled={!hasNext}
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#3D1C1A] text-[#FEF5E6] font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3D1C1A]/90 transition-colors text-sm"
        >
          Siguiente
          <ChevronRight data-component-id="src/components/ui/SearchView.tsx:135:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="135" data-component-file="SearchView.tsx" data-component-name="ChevronRight" data-component-content="%7B%22elementName%22%3A%22ChevronRight%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const renderProductItem = (product: ProductBase) => {
    const currentPriceNumber = parseFloat(String(product?.priceFinalInc));
    const originalPriceNumber =
      product?.priceOriginalInc !== undefined
        ? parseFloat(String(product?.priceOriginalInc))
        : undefined;
    const hasDiscount =
      originalPriceNumber !== undefined &&
      !isNaN(originalPriceNumber) &&
      originalPriceNumber > currentPriceNumber;
    const discountPercent = hasDiscount
      ? Math.round(((originalPriceNumber - currentPriceNumber) / originalPriceNumber) * 100)
      : 0;
    return (
      <Link data-component-id="src/components/ui/SearchView.tsx:155:6" data-component-path="src/components/ui/SearchView.tsx" data-component-line="155" data-component-file="SearchView.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20p-3%20hover%3Abg-%5B%233D1C1A%5D%2F10%20transition-colors%20border-b%20border-%5B%233D1C1A%5D%2F10%20last%3Aborder-b-0%20group%22%7D"
        key={product.id}
        href={product.url || ''}
        onClick={() => handleProductClick(product)}
        className="flex items-center gap-3 p-3 hover:bg-[#3D1C1A]/10 transition-colors border-b border-[#3D1C1A]/10 last:border-b-0 group"
      >
        <div data-component-id="src/components/ui/SearchView.tsx:161:8" data-component-path="src/components/ui/SearchView.tsx" data-component-line="161" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20w-12%20h-12%20relative%22%7D" className="flex-shrink-0 w-12 h-12 relative">
          <StockBadge data-component-id="src/components/ui/SearchView.tsx:162:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="162" data-component-file="SearchView.tsx" data-component-name="StockBadge" data-component-content="%7B%22elementName%22%3A%22StockBadge%22%2C%22className%22%3A%22top-5%20right-0%20left-auto%22%7D" productId={product.id} variant="compact" className="top-5 right-0 left-auto" />
          <Image data-component-id="src/components/ui/SearchView.tsx:163:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="163" data-component-file="SearchView.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22w-full%20h-full%20object-contain%20rounded%22%7D"
            src={product.image || ''}
            alt={product.title || ''}
            width={48}
            height={48}
            className="w-full h-full object-contain rounded"
          />
          {hasDiscount && (
            <div data-component-id="src/components/ui/SearchView.tsx:171:12" data-component-path="src/components/ui/SearchView.tsx" data-component-line="171" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20-top-1%20-right-1%20bg-%5B%23fcc00a%5D%20text-%5B%233D1C1A%5D%20text-xs%20font-bold%20rounded-full%20w-5%20h-5%20flex%20items-center%20justify-center%22%2C%22textContent%22%3A%22-%20%25%22%7D" className="absolute -top-1 -right-1 bg-[#fcc00a] text-[#3D1C1A] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              -{discountPercent}%
            </div>
          )}
        </div>
        <div data-component-id="src/components/ui/SearchView.tsx:176:8" data-component-path="src/components/ui/SearchView.tsx" data-component-line="176" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-grow%20min-w-0%22%7D" className="flex-grow min-w-0">
          <h3 data-component-id="src/components/ui/SearchView.tsx:177:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="177" data-component-file="SearchView.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-sm%20font-medium%20text-%5B%233D1C1A%5D%20truncate%20group-hover%3Atext-%5B%233D1C1A%5D%2F80%20transition-colors%22%7D"
            className="text-sm font-medium text-[#3D1C1A] truncate group-hover:text-[#3D1C1A]/80 transition-colors"
            style={{
              fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
              fontWeight: '700',
            }}
          >
            {product.title}
          </h3>
          <p data-component-id="src/components/ui/SearchView.tsx:186:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="186" data-component-file="SearchView.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%233D1C1A%5D%2F70%20truncate%22%7D"
            className="text-xs text-[#3D1C1A]/70 truncate"
            style={{
              fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
              fontWeight: '400',
            }}
          >
            {product.brand}
          </p>
          {product.productCombinations && product.productCombinations.length > 0 && (
            <p data-component-id="src/components/ui/SearchView.tsx:196:12" data-component-path="src/components/ui/SearchView.tsx" data-component-line="196" data-component-file="SearchView.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%233D1C1A%5D%2F60%20truncate%22%7D" className="text-xs text-[#3D1C1A]/60 truncate">
              {product.productCombinations
                .find((combination) => combination.isDefault)
                ?.productOptions?.map((option) => option.name)
                .join(', ')}
            </p>
          )}
        </div>
        <div data-component-id="src/components/ui/SearchView.tsx:204:8" data-component-path="src/components/ui/SearchView.tsx" data-component-line="204" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20text-right%22%7D" className="flex-shrink-0 text-right">
          <div data-component-id="src/components/ui/SearchView.tsx:205:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="205" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20font-bold%20text-%5B%233D1C1A%5D%22%7D"
            className="text-sm font-bold text-[#3D1C1A]"
            style={{
              fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
              fontWeight: '700',
            }}
          >
            {formatPrice(product.priceFinalInc)}
          </div>
          {hasDiscount && (
            <div data-component-id="src/components/ui/SearchView.tsx:215:12" data-component-path="src/components/ui/SearchView.tsx" data-component-line="215" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-%5B%233D1C1A%5D%2F60%20line-through%22%7D" className="text-xs text-[#3D1C1A]/60 line-through">
              {formatPrice(originalPriceNumber)}
            </div>
          )}
        </div>
      </Link>
    );
  };

  const renderDropdown = () => {
    if (!showDropdown) return null;
    return (
      <div data-component-id="src/components/ui/SearchView.tsx:227:6" data-component-path="src/components/ui/SearchView.tsx" data-component-line="227" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20left-0%20right-0%20mt-1%20bg-%5B%23FEF5E6%5D%20border%20border-%5B%233D1C1A%5D%2F20%20rounded-lg%20shadow-lg%20z-50%20max-h-96%20overflow-y-auto%20scrollbar-hide%22%7D"
        ref={dropdownRef}
        className="absolute top-full left-0 right-0 mt-1 bg-[#FEF5E6] border border-[#3D1C1A]/20 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto scrollbar-hide"
        style={{ boxShadow: '0 10px 25px rgba(61, 28, 26, 0.15)' }}
      >
        {isLoading ? (
          <div data-component-id="src/components/ui/SearchView.tsx:233:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="233" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20p-4%22%7D" className="flex items-center justify-center p-4">
            <div data-component-id="src/components/ui/SearchView.tsx:234:12" data-component-path="src/components/ui/SearchView.tsx" data-component-line="234" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-spin%20rounded-full%20h-6%20w-6%20border-b-2%20border-%5B%233D1C1A%5D%22%7D" className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3D1C1A]"></div>
            <span data-component-id="src/components/ui/SearchView.tsx:235:12" data-component-path="src/components/ui/SearchView.tsx" data-component-line="235" data-component-file="SearchView.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-2%20text-sm%20text-%5B%233D1C1A%5D%20font-medium%22%2C%22textContent%22%3A%22Buscando...%22%7D" className="ml-2 text-sm text-[#3D1C1A] font-medium">Buscando...</span>
          </div>
        ) : error ? (
          <div data-component-id="src/components/ui/SearchView.tsx:238:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="238" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20text-center%20text-red-600%20text-sm%20font-medium%22%2C%22textContent%22%3A%22Error%20al%20buscar%20productos%22%7D" className="p-4 text-center text-red-600 text-sm font-medium">
            Error al buscar productos
          </div>
        ) : !searchResults?.data || searchResults.data.length === 0 ? (
          <div data-component-id="src/components/ui/SearchView.tsx:242:10" data-component-path="src/components/ui/SearchView.tsx" data-component-line="242" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20text-center%20text-%5B%233D1C1A%5D%2F60%20text-sm%20font-medium%22%2C%22textContent%22%3A%22No%20se%20encontraron%20productos%22%7D" className="p-4 text-center text-[#3D1C1A]/60 text-sm font-medium">
            No se encontraron productos
          </div>
        ) : (
          <>
            {searchResults.data.map(renderProductItem)}
            {renderPagination()}
            {searchResults.pagination.total > 1 && (
              <div data-component-id="src/components/ui/SearchView.tsx:250:14" data-component-path="src/components/ui/SearchView.tsx" data-component-line="250" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20text-center%20border-t%20border-%5B%233D1C1A%5D%2F20%20bg-%5B%23FEF5E6%5D%22%7D" className="p-3 text-center border-t border-[#3D1C1A]/20 bg-[#FEF5E6]">
                <span data-component-id="src/components/ui/SearchView.tsx:251:16" data-component-path="src/components/ui/SearchView.tsx" data-component-line="251" data-component-file="SearchView.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-%5B%233D1C1A%5D%2F60%20font-medium%22%2C%22textContent%22%3A%22Mostrando%20de%20%20%20resultados%22%7D" className="text-xs text-[#3D1C1A]/60 font-medium">
                  Mostrando {searchResults.data.length} de {searchResults.pagination.total}{' '}
                  resultados
                </span>
                
                <div data-component-id="src/components/ui/SearchView.tsx:256:16" data-component-path="src/components/ui/SearchView.tsx" data-component-line="256" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%22%7D" className="mt-2">
                  <Link data-component-id="src/components/ui/SearchView.tsx:257:18" data-component-path="src/components/ui/SearchView.tsx" data-component-line="257" data-component-file="SearchView.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-%5B%233D1C1A%5D%20underline%20hover%3Aopacity-80%20transition%22%2C%22textContent%22%3A%22Mostrar%20todo%22%7D"
                    href={`/busqueda?q=${encodeURIComponent(currentValue.trim())}&page=1`}
                    onClick={() => {
                      setShowDropdown(false);
                      setIsFocused(false);
                    }}
                    className="text-sm font-semibold text-[#3D1C1A] underline hover:opacity-80 transition"
                  >
                    Mostrar todo
                  </Link>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div data-component-id="src/components/ui/SearchView.tsx:277:4" data-component-path="src/components/ui/SearchView.tsx" data-component-line="277" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`}>
      <div data-component-id="src/components/ui/SearchView.tsx:278:6" data-component-path="src/components/ui/SearchView.tsx" data-component-line="278" data-component-file="SearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-center%20bg-searchview-1%20rounded-%5B10px%5D%20px-6%20py-4%22%7D" className="flex flex-row items-center bg-searchview-1 rounded-[10px] px-6 py-4">
        <Image data-component-id="src/components/ui/SearchView.tsx:279:8" data-component-path="src/components/ui/SearchView.tsx" data-component-line="279" data-component-file="SearchView.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fimg_search.svg%22%2C%22alt%22%3A%22Search%22%2C%22className%22%3A%22mr-6%22%7D" src="/images/img_search.svg" alt="Search" width={24} height={24} className="mr-6" />
        <input data-component-id="src/components/ui/SearchView.tsx:280:8" data-component-path="src/components/ui/SearchView.tsx" data-component-line="280" data-component-file="SearchView.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AcurrentValue%5D%22%2C%22className%22%3A%22flex-1%20bg-transparent%20text-searchview-1%20font-tt-norms-medium%20text-base%20leading-5%20outline-none%20placeholder-%5B%233D1C1A%5D%2F50%22%7D"
          ref={inputRef}
          type="text"
          value={currentValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-searchview-1 font-tt-norms-medium text-base leading-5 outline-none placeholder-[#3D1C1A]/50"
        />
      </div>
      {renderDropdown()}
    </div>
  );
};

export default SearchView;
